import React from 'react';
import { ScrollView, TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useHover } from '@hooks/useHover';
import Images from '@themes/images';
import theme from '@themes/theme';

import type { IconSizes } from '@components/shared/Icon/RestyleIcon';

interface ProjectIconGridProps {
  bodyWidth: number;
  selectedIcon?: keyof typeof Images;
  list: (keyof typeof Images)[];
  onPress: (icon: keyof typeof Images) => void;
  // Figma (TA-10404) specs a 48px cell / 32px icon / 24px gap for the web
  // popover; those are the defaults here. Mobile's IconPicker.tsx overrides
  // cellGap and iconVariant back down to its own pre-existing, tighter
  // values — the web spec's 24px gap, applied to a phone-width bodyWidth,
  // cut mobile's columns from ~6 to 5 and pushed all 40 icons from ~6 rows
  // to 8, a real usability regression (confirmed on-device). cellSize and
  // cellPadding stay available for a future caller that needs them, but no
  // current caller overrides them — the default 48px cell at a tighter gap
  // already reproduces mobile's original density.
  cellSize?: number;
  cellGap?: number;
  cellPadding?: number;
  iconVariant?: IconSizes;
}

const DEFAULT_CELL_SIZE = theme.spacing.xxl;
const DEFAULT_CELL_GAP = theme.spacing.l;
const DEFAULT_CELL_PADDING = theme.spacing.xs;
const DEFAULT_ICON_VARIANT: IconSizes = 'xxl';

interface IconGridItemProps {
  item: keyof typeof Images;
  selected: boolean;
  cellSize: number;
  cellPadding: number;
  iconVariant: IconSizes;
  marginRight: number;
  marginTop: number;
  onPress: () => void;
}

// TA-10950 #20: Grey01 hover per item, extracted into its own component so
// useHover isn't called inside the list's .map() callback (Rules of Hooks) -
// mirrors PrioritySelector.web.tsx's PriorityRow. useHover is a web-only
// no-op on native, so this is safe in this file shared with the mobile
// icon picker.
const IconGridItem: React.FC<IconGridItemProps> = ({
  item,
  selected,
  cellSize,
  cellPadding,
  iconVariant,
  marginRight,
  marginTop,
  onPress,
}) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <TouchableOpacity
      {...hoverProps}
      accessibilityRole='button'
      accessibilityLabel={`project icon ${item}`}
      accessibilityState={{ selected }}
      onPress={onPress}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        width: cellSize,
        height: cellSize,
        marginRight,
        marginTop,
        borderRadius: theme.borderRadii.s,
        padding: cellPadding,
        backgroundColor:
          selected || isHovered ? theme.colors.grey01 : undefined,
      }}>
      <Icon
        name={item}
        variant={iconVariant}
        color={selected ? 'black' : 'textSecondary'}
      />
    </TouchableOpacity>
  );
};

export const ProjectIconGrid: React.FC<ProjectIconGridProps> = ({
  bodyWidth,
  selectedIcon,
  list,
  onPress,
  cellSize = DEFAULT_CELL_SIZE,
  cellGap = DEFAULT_CELL_GAP,
  cellPadding = DEFAULT_CELL_PADDING,
  iconVariant = DEFAULT_ICON_VARIANT,
}) => {
  // Derived from bodyWidth rather than accepted as a prop: a caller-supplied
  // column count can silently drift out of sync with what bodyWidth actually
  // wraps to (TA-10404 — this broke both the web picker's row alignment and,
  // separately, the mobile picker which reuses this component with a very
  // different bodyWidth), producing overlapping rows or ragged edges.
  const columns = Math.max(
    1,
    Math.floor((bodyWidth + cellGap) / (cellSize + cellGap))
  );

  return (
    <Box width={bodyWidth} alignContent='center' justifyContent='center'>
      <ScrollView showsVerticalScrollIndicator={false}>
        <Box flexDirection='row' flexWrap='wrap'>
          {list.map((item, index) => (
            <IconGridItem
              key={item}
              item={item}
              selected={selectedIcon === item}
              cellSize={cellSize}
              cellPadding={cellPadding}
              iconVariant={iconVariant}
              marginRight={(index + 1) % columns ? cellGap : 0}
              marginTop={index < columns ? 0 : cellGap}
              onPress={() => onPress(item)}
            />
          ))}
        </Box>
      </ScrollView>
    </Box>
  );
};
